/**
 * The encounter prepared for the current session, if there is one.
 *
 * Kept out of useSession's reducer deliberately. An encounter is not part of
 * the storyboard state machine — it arrives on its own event, it is at most
 * one per session, and it is fetched rather than accumulated. Threading it
 * through the checkpoint reducer would tangle two unrelated lifecycles.
 *
 * The `encounter.ready` event carries a summary, not the full design, so the
 * event is used as a signal to refetch rather than as the payload. That keeps
 * one shape — the API's — instead of two that can drift.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Encounter } from '@/api/combat';
import type { DiscardKind } from '@/api/client';
import {
  getSessionEncounter,
  setEncounterStatus,
  userFacingError,
} from '@/api/client';

export interface EncounterState {
  encounter: Encounter | null;
  error: string | null;
  /** Open on the combat board rather than the storyboard. */
  open: boolean;
  setOpen: (open: boolean) => void;
  refresh: () => Promise<void>;
  /** Mark the fight started — the GM walked into it. */
  begin: () => Promise<void>;
  /** The GM accepted the pitch; the full build starts. */
  accept: () => Promise<void>;
  /** The fight is over, which frees the session to prepare the next one. */
  resolve: () => Promise<void>;
  /**
   * The GM said no. `kind` and `note` are what they said about it — a
   * refusal with no reason taught the system exactly as much as a fight
   * nobody looked at, and a GM who was pitched four fights and refused three
   * got no combat at all because nothing could act on the refusals.
   */
  discard: (refusal?: { kind?: DiscardKind; note?: string }) => Promise<void>;
}

export function useEncounter(sessionId: string | null): EncounterState {
  const [encounter, setEncounter] = useState<Encounter | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const moving = useRef(false);
  const currentSession = useRef(sessionId);
  currentSession.current = sessionId;

  const refresh = useCallback(async () => {
    if (!sessionId) {
      setEncounter(null);
      return;
    }
    try {
      const { encounter: found } = await getSessionEncounter(sessionId);
      if (currentSession.current === sessionId) setEncounter(found);
    } catch {
      // Non-fatal. A missing encounter must never take the session view down
      // with it — the GM still needs the storyboard and the transcript.
    }
  }, [sessionId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Leaving a session must close the board, or the next session opens showing
  // the previous one's fight.
  useEffect(() => {
    setOpen(false);
    setEncounter(null);
    setError(null);
  }, [sessionId]);

  const move = useCallback(
    async (
      status: 'building' | 'running' | 'resolved' | 'discarded',
      refusal?: { kind?: DiscardKind; note?: string },
    ) => {
      if (!encounter || moving.current) return;
      const sessionAtStart = currentSession.current;
      moving.current = true;
      setError(null);
      try {
        const { encounter: updated } = await setEncounterStatus(
          encounter.id,
          status,
          refusal,
        );
        if (currentSession.current !== sessionAtStart) return;
        // A resolved or discarded encounter is gone from the GM's view; the
        // session is free to have another prepared.
        // Accepting keeps the card on screen — it becomes the "building"
        // state. Only a finished or refused fight leaves.
        setEncounter(
          status === 'running' || status === 'building' ? updated : null,
        );
        if (status !== 'running') setOpen(false);
      } catch (err) {
        if (currentSession.current === sessionAtStart)
          setError(
            userFacingError(
              err,
              'That encounter change wasn’t saved. Please try again.',
            ),
          );
      } finally {
        moving.current = false;
      }
    },
    [encounter],
  );

  return {
    encounter,
    error,
    open,
    setOpen,
    refresh,
    begin: useCallback(() => move('running'), [move]),
    accept: useCallback(() => move('building'), [move]),
    resolve: useCallback(() => move('resolved'), [move]),
    discard: useCallback(
      (refusal?: { kind?: DiscardKind; note?: string }) =>
        move('discarded', refusal),
      [move],
    ),
  };
}
